export type TourStepDef = {
  /** Matches data-tour="..." on the target element */
  anchor: string;
  /** Optional route to navigate to before highlighting */
  route?: string;
  titleKey: string;
  bodyKey: string;
};

export type TourDef = {
  id: string;
  /** Roles allowed to run this tour */
  roles: Array<'User' | 'TenantAdmin' | 'SuperAdmin' | 'Supervisor'>;
  /** Entry route if not already there */
  entryRoute: string;
  steps: TourStepDef[];
};

export const TOURS: Record<string, TourDef> = {
  'user-quick-start': {
    id: 'user-quick-start',
    roles: ['User'],
    entryRoute: '/my-attendance',
    steps: [
      {
        anchor: 'nav-my-attendance',
        route: '/my-attendance',
        titleKey: 'tours.quickStart.step1Title',
        bodyKey: 'tours.quickStart.step1Body',
      },
      {
        anchor: 'my-attendance-today',
        titleKey: 'tours.quickStart.step2Title',
        bodyKey: 'tours.quickStart.step2Body',
      },
      {
        anchor: 'my-attendance-check-in',
        titleKey: 'tours.quickStart.step3Title',
        bodyKey: 'tours.quickStart.step3Body',
      },
      {
        anchor: 'nav-my-schedule',
        route: '/my-schedule',
        titleKey: 'tours.quickStart.step4Title',
        bodyKey: 'tours.quickStart.step4Body',
      },
    ],
  },
  'user-login': {
    id: 'user-login',
    roles: ['User', 'TenantAdmin', 'SuperAdmin', 'Supervisor'],
    entryRoute: '/profile',
    steps: [
      {
        anchor: 'profile-tab-security',
        route: '/profile',
        titleKey: 'tours.login.step1Title',
        bodyKey: 'tours.login.step1Body',
      },
      {
        anchor: 'profile-change-password',
        titleKey: 'tours.login.step2Title',
        bodyKey: 'tours.login.step2Body',
      },
    ],
  },
  'user-check-in-out': {
    id: 'user-check-in-out',
    roles: ['User'],
    entryRoute: '/my-attendance',
    steps: [
      {
        anchor: 'my-attendance-today',
        route: '/my-attendance',
        titleKey: 'tours.checkIn.step1Title',
        bodyKey: 'tours.checkIn.step1Body',
      },
      {
        anchor: 'my-attendance-check-in',
        titleKey: 'tours.checkIn.step2Title',
        bodyKey: 'tours.checkIn.step2Body',
      },
      {
        anchor: 'my-attendance-check-out',
        titleKey: 'tours.checkIn.step3Title',
        bodyKey: 'tours.checkIn.step3Body',
      },
      {
        anchor: 'my-attendance-punches',
        titleKey: 'tours.checkIn.step4Title',
        bodyKey: 'tours.checkIn.step4Body',
      },
    ],
  },
  'user-correction-request': {
    id: 'user-correction-request',
    roles: ['User'],
    entryRoute: '/my-attendance',
    steps: [
      {
        anchor: 'my-attendance-request',
        route: '/my-attendance',
        titleKey: 'tours.correction.step1Title',
        bodyKey: 'tours.correction.step1Body',
      },
      {
        anchor: 'my-attendance-requests',
        titleKey: 'tours.correction.step2Title',
        bodyKey: 'tours.correction.step2Body',
      },
    ],
  },
  'user-my-schedule': {
    id: 'user-my-schedule',
    roles: ['User'],
    entryRoute: '/my-schedule',
    steps: [
      {
        anchor: 'my-schedule-view-week',
        route: '/my-schedule',
        titleKey: 'tours.schedule.step1Title',
        bodyKey: 'tours.schedule.step1Body',
      },
      {
        anchor: 'my-schedule-view-month',
        titleKey: 'tours.schedule.step2Title',
        bodyKey: 'tours.schedule.step2Body',
      },
      {
        anchor: 'my-schedule-print',
        titleKey: 'tours.schedule.step3Title',
        bodyKey: 'tours.schedule.step3Body',
      },
    ],
  },

  // —— Admin P0 ——
  'admin-morning-queue': {
    id: 'admin-morning-queue',
    roles: ['TenantAdmin', 'SuperAdmin', 'Supervisor'],
    entryRoute: '/shifts/reviews',
    steps: [
      {
        anchor: 'reviews-page-header',
        route: '/shifts/reviews',
        titleKey: 'tours.adminMorning.step1Title',
        bodyKey: 'tours.adminMorning.step1Body',
      },
      {
        anchor: 'reviews-queue-status',
        titleKey: 'tours.adminMorning.step2Title',
        bodyKey: 'tours.adminMorning.step2Body',
      },
      {
        anchor: 'reviews-queue-list',
        titleKey: 'tours.adminMorning.step3Title',
        bodyKey: 'tours.adminMorning.step3Body',
      },
      {
        anchor: 'reviews-filters',
        titleKey: 'tours.adminMorning.step4Title',
        bodyKey: 'tours.adminMorning.step4Body',
      },
    ],
  },
  'admin-schedule-publish': {
    id: 'admin-schedule-publish',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/shifts/schedule',
    steps: [
      {
        anchor: 'schedule-week-grid',
        route: '/shifts/schedule',
        titleKey: 'tours.adminSchedule.step1Title',
        bodyKey: 'tours.adminSchedule.step1Body',
      },
      {
        anchor: 'schedule-validate',
        titleKey: 'tours.adminSchedule.step2Title',
        bodyKey: 'tours.adminSchedule.step2Body',
      },
      {
        anchor: 'schedule-publish',
        titleKey: 'tours.adminSchedule.step3Title',
        bodyKey: 'tours.adminSchedule.step3Body',
      },
      {
        anchor: 'schedule-lock',
        titleKey: 'tours.adminSchedule.step4Title',
        bodyKey: 'tours.adminSchedule.step4Body',
      },
    ],
  },
  'admin-devices': {
    id: 'admin-devices',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/devices',
    steps: [
      {
        anchor: 'devices-stats',
        route: '/devices',
        titleKey: 'tours.adminDevices.step1Title',
        bodyKey: 'tours.adminDevices.step1Body',
      },
      {
        anchor: 'devices-table',
        titleKey: 'tours.adminDevices.step2Title',
        bodyKey: 'tours.adminDevices.step2Body',
      },
    ],
  },
  'admin-sync': {
    id: 'admin-sync',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/sync',
    steps: [
      {
        anchor: 'sync-stats',
        route: '/sync',
        titleKey: 'tours.adminSync.step1Title',
        bodyKey: 'tours.adminSync.step1Body',
      },
      {
        anchor: 'sync-jobs-table',
        titleKey: 'tours.adminSync.step2Title',
        bodyKey: 'tours.adminSync.step2Body',
      },
    ],
  },
  'admin-users': {
    id: 'admin-users',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/users',
    steps: [
      {
        anchor: 'users-add',
        route: '/users',
        titleKey: 'tours.adminUsers.step1Title',
        bodyKey: 'tours.adminUsers.step1Body',
      },
      {
        anchor: 'users-table',
        titleKey: 'tours.adminUsers.step2Title',
        bodyKey: 'tours.adminUsers.step2Body',
      },
    ],
  },
  'admin-assignments': {
    id: 'admin-assignments',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/assignments',
    steps: [
      {
        anchor: 'assignments-device-list',
        route: '/assignments',
        titleKey: 'tours.adminAssignments.step1Title',
        bodyKey: 'tours.adminAssignments.step1Body',
      },
      {
        anchor: 'assignments-people-panel',
        titleKey: 'tours.adminAssignments.step2Title',
        bodyKey: 'tours.adminAssignments.step2Body',
      },
    ],
  },
  'admin-pay-period': {
    id: 'admin-pay-period',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/shifts/pay-periods',
    steps: [
      {
        anchor: 'pay-periods-create',
        route: '/shifts/pay-periods',
        titleKey: 'tours.adminPayPeriod.step1Title',
        bodyKey: 'tours.adminPayPeriod.step1Body',
      },
      {
        anchor: 'pay-periods-metrics',
        titleKey: 'tours.adminPayPeriod.step2Title',
        bodyKey: 'tours.adminPayPeriod.step2Body',
      },
      {
        anchor: 'pay-periods-table',
        titleKey: 'tours.adminPayPeriod.step3Title',
        bodyKey: 'tours.adminPayPeriod.step3Body',
      },
    ],
  },

  // —— Admin P1 ——
  'admin-dashboard-daily': {
    id: 'admin-dashboard-daily',
    roles: ['TenantAdmin', 'SuperAdmin', 'Supervisor'],
    entryRoute: '/dashboard',
    steps: [
      {
        anchor: 'dashboard-insight',
        route: '/dashboard',
        titleKey: 'tours.adminDashboard.step1Title',
        bodyKey: 'tours.adminDashboard.step1Body',
      },
      {
        anchor: 'dashboard-attention',
        titleKey: 'tours.adminDashboard.step2Title',
        bodyKey: 'tours.adminDashboard.step2Body',
      },
      {
        anchor: 'dashboard-health',
        titleKey: 'tours.adminDashboard.step3Title',
        bodyKey: 'tours.adminDashboard.step3Body',
      },
    ],
  },
  'admin-templates-publish': {
    id: 'admin-templates-publish',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/shifts/templates',
    steps: [
      {
        anchor: 'templates-add',
        route: '/shifts/templates',
        titleKey: 'tours.adminTemplates.step1Title',
        bodyKey: 'tours.adminTemplates.step1Body',
      },
      {
        anchor: 'templates-table',
        titleKey: 'tours.adminTemplates.step2Title',
        bodyKey: 'tours.adminTemplates.step2Body',
      },
    ],
  },
  'admin-schedule-exceptions': {
    id: 'admin-schedule-exceptions',
    roles: ['TenantAdmin', 'SuperAdmin', 'Supervisor'],
    entryRoute: '/shifts/exceptions',
    steps: [
      {
        anchor: 'exceptions-add',
        route: '/shifts/exceptions',
        titleKey: 'tours.adminExceptions.step1Title',
        bodyKey: 'tours.adminExceptions.step1Body',
      },
      {
        anchor: 'exceptions-filters',
        titleKey: 'tours.adminExceptions.step2Title',
        bodyKey: 'tours.adminExceptions.step2Body',
      },
      {
        anchor: 'exceptions-table',
        titleKey: 'tours.adminExceptions.step3Title',
        bodyKey: 'tours.adminExceptions.step3Body',
      },
    ],
  },
  'admin-crews': {
    id: 'admin-crews',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/shifts/crews',
    steps: [
      {
        anchor: 'crews-add',
        route: '/shifts/crews',
        titleKey: 'tours.adminCrews.step1Title',
        bodyKey: 'tours.adminCrews.step1Body',
      },
      {
        anchor: 'crews-filters',
        titleKey: 'tours.adminCrews.step2Title',
        bodyKey: 'tours.adminCrews.step2Body',
      },
      {
        anchor: 'crews-table',
        titleKey: 'tours.adminCrews.step3Title',
        bodyKey: 'tours.adminCrews.step3Body',
      },
    ],
  },
  'admin-adjustments-approve': {
    id: 'admin-adjustments-approve',
    roles: ['TenantAdmin', 'SuperAdmin', 'Supervisor'],
    entryRoute: '/shifts/adjustments',
    steps: [
      {
        anchor: 'adjustments-create',
        route: '/shifts/adjustments',
        titleKey: 'tours.adminAdjustments.step1Title',
        bodyKey: 'tours.adminAdjustments.step1Body',
      },
      {
        anchor: 'adjustments-metrics',
        titleKey: 'tours.adminAdjustments.step2Title',
        bodyKey: 'tours.adminAdjustments.step2Body',
      },
      {
        anchor: 'adjustments-table',
        titleKey: 'tours.adminAdjustments.step3Title',
        bodyKey: 'tours.adminAdjustments.step3Body',
      },
    ],
  },
  'admin-four-eyes': {
    id: 'admin-four-eyes',
    roles: ['TenantAdmin', 'SuperAdmin', 'Supervisor'],
    entryRoute: '/shifts/adjustments',
    steps: [
      {
        anchor: 'adjustments-filters',
        route: '/shifts/adjustments',
        titleKey: 'tours.adminFourEyes.step1Title',
        bodyKey: 'tours.adminFourEyes.step1Body',
      },
      {
        anchor: 'adjustments-table',
        titleKey: 'tours.adminFourEyes.step2Title',
        bodyKey: 'tours.adminFourEyes.step2Body',
      },
      {
        anchor: 'adjustments-create',
        titleKey: 'tours.adminFourEyes.step3Title',
        bodyKey: 'tours.adminFourEyes.step3Body',
      },
    ],
  },
  'admin-work-rules': {
    id: 'admin-work-rules',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/shifts/work-rules',
    steps: [
      {
        anchor: 'work-rules-header',
        route: '/shifts/work-rules',
        titleKey: 'tours.adminWorkRules.step1Title',
        bodyKey: 'tours.adminWorkRules.step1Body',
      },
      {
        anchor: 'work-rules-save',
        titleKey: 'tours.adminWorkRules.step2Title',
        bodyKey: 'tours.adminWorkRules.step2Body',
      },
    ],
  },
  'admin-holidays': {
    id: 'admin-holidays',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/shifts/holidays',
    steps: [
      {
        anchor: 'holidays-add',
        route: '/shifts/holidays',
        titleKey: 'tours.adminHolidays.step1Title',
        bodyKey: 'tours.adminHolidays.step1Body',
      },
      {
        anchor: 'holidays-import',
        titleKey: 'tours.adminHolidays.step2Title',
        bodyKey: 'tours.adminHolidays.step2Body',
      },
    ],
  },
  'admin-reports-hub': {
    id: 'admin-reports-hub',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/reports',
    steps: [
      {
        anchor: 'reports-hub',
        route: '/reports',
        titleKey: 'tours.adminReports.step1Title',
        bodyKey: 'tours.adminReports.step1Body',
      },
      {
        anchor: 'reports-missing-card',
        titleKey: 'tours.adminReports.step2Title',
        bodyKey: 'tours.adminReports.step2Body',
      },
      {
        anchor: 'reports-aging-card',
        titleKey: 'tours.adminReports.step3Title',
        bodyKey: 'tours.adminReports.step3Body',
      },
    ],
  },
  'admin-locations-timezone': {
    id: 'admin-locations-timezone',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/locations',
    steps: [
      {
        anchor: 'locations-add',
        route: '/locations',
        titleKey: 'tours.adminLocations.step1Title',
        bodyKey: 'tours.adminLocations.step1Body',
      },
      {
        anchor: 'locations-filters',
        titleKey: 'tours.adminLocations.step2Title',
        bodyKey: 'tours.adminLocations.step2Body',
      },
      {
        anchor: 'locations-table',
        titleKey: 'tours.adminLocations.step3Title',
        bodyKey: 'tours.adminLocations.step3Body',
      },
    ],
  },
  'admin-permanent-access': {
    id: 'admin-permanent-access',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/assignments/policies',
    steps: [
      {
        anchor: 'policies-header',
        route: '/assignments/policies',
        titleKey: 'tours.adminPolicies.step1Title',
        bodyKey: 'tours.adminPolicies.step1Body',
      },
      {
        anchor: 'policies-create',
        titleKey: 'tours.adminPolicies.step2Title',
        bodyKey: 'tours.adminPolicies.step2Body',
      },
      {
        anchor: 'policies-list',
        titleKey: 'tours.adminPolicies.step3Title',
        bodyKey: 'tours.adminPolicies.step3Body',
      },
    ],
  },
  'admin-self-service-setup': {
    id: 'admin-self-service-setup',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/users',
    steps: [
      {
        anchor: 'users-add',
        route: '/users',
        titleKey: 'tours.adminSelfService.step1Title',
        bodyKey: 'tours.adminSelfService.step1Body',
      },
      {
        anchor: 'users-table',
        titleKey: 'tours.adminSelfService.step2Title',
        bodyKey: 'tours.adminSelfService.step2Body',
      },
    ],
  },

  // —— Platform P1 ——
  'platform-tenant-create': {
    id: 'platform-tenant-create',
    roles: ['SuperAdmin'],
    entryRoute: '/tenants',
    steps: [
      {
        anchor: 'nav-tenants',
        route: '/tenants',
        titleKey: 'tours.platformTenant.step1Title',
        bodyKey: 'tours.platformTenant.step1Body',
      },
      {
        anchor: 'tenants-add',
        titleKey: 'tours.platformTenant.step2Title',
        bodyKey: 'tours.platformTenant.step2Body',
      },
      {
        anchor: 'tenants-table',
        titleKey: 'tours.platformTenant.step3Title',
        bodyKey: 'tours.platformTenant.step3Body',
      },
      {
        anchor: 'tenants-add-admin',
        titleKey: 'tours.platformTenant.step4Title',
        bodyKey: 'tours.platformTenant.step4Body',
      },
    ],
  },
  'platform-feature-flags': {
    id: 'platform-feature-flags',
    roles: ['SuperAdmin'],
    entryRoute: '/tenants',
    steps: [
      {
        anchor: 'tenants-filters',
        route: '/tenants',
        titleKey: 'tours.platformFeatures.step1Title',
        bodyKey: 'tours.platformFeatures.step1Body',
      },
      {
        anchor: 'tenants-manage-features',
        titleKey: 'tours.platformFeatures.step2Title',
        bodyKey: 'tours.platformFeatures.step2Body',
      },
      {
        anchor: 'features-shifts',
        titleKey: 'tours.platformFeatures.step3Title',
        bodyKey: 'tours.platformFeatures.step3Body',
      },
      {
        anchor: 'features-limits',
        titleKey: 'tours.platformFeatures.step4Title',
        bodyKey: 'tours.platformFeatures.step4Body',
      },
      {
        anchor: 'features-save',
        titleKey: 'tours.platformFeatures.step5Title',
        bodyKey: 'tours.platformFeatures.step5Body',
      },
    ],
  },

  // —— User P2 ——
  'user-profile-preferences': {
    id: 'user-profile-preferences',
    roles: ['User', 'TenantAdmin', 'SuperAdmin', 'Supervisor'],
    entryRoute: '/profile',
    steps: [
      {
        anchor: 'profile-tab-settings',
        route: '/profile',
        titleKey: 'tours.userPreferences.step1Title',
        bodyKey: 'tours.userPreferences.step1Body',
      },
      {
        anchor: 'profile-language',
        titleKey: 'tours.userPreferences.step2Title',
        bodyKey: 'tours.userPreferences.step2Body',
      },
      {
        anchor: 'profile-settings-save',
        titleKey: 'tours.userPreferences.step3Title',
        bodyKey: 'tours.userPreferences.step3Body',
      },
    ],
  },
  'user-request-status': {
    id: 'user-request-status',
    roles: ['User'],
    entryRoute: '/my-attendance',
    steps: [
      {
        anchor: 'my-attendance-request',
        route: '/my-attendance',
        titleKey: 'tours.userRequestStatus.step1Title',
        bodyKey: 'tours.userRequestStatus.step1Body',
      },
      {
        anchor: 'my-attendance-requests',
        titleKey: 'tours.userRequestStatus.step2Title',
        bodyKey: 'tours.userRequestStatus.step2Body',
      },
      {
        anchor: 'my-attendance-request-status',
        titleKey: 'tours.userRequestStatus.step3Title',
        bodyKey: 'tours.userRequestStatus.step3Body',
      },
    ],
  },

  // —— Admin P2 ——
  'admin-rotations': {
    id: 'admin-rotations',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/shifts/rotations',
    steps: [
      {
        anchor: 'nav-shift-rotations',
        route: '/shifts/rotations',
        titleKey: 'tours.adminRotations.step1Title',
        bodyKey: 'tours.adminRotations.step1Body',
      },
      {
        anchor: 'rotations-add',
        titleKey: 'tours.adminRotations.step2Title',
        bodyKey: 'tours.adminRotations.step2Body',
      },
      {
        anchor: 'rotations-table',
        titleKey: 'tours.adminRotations.step3Title',
        bodyKey: 'tours.adminRotations.step3Body',
      },
    ],
  },
  'admin-worker-heartbeat': {
    id: 'admin-worker-heartbeat',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/workers',
    steps: [
      {
        anchor: 'nav-workers',
        route: '/workers',
        titleKey: 'tours.adminWorkers.step1Title',
        bodyKey: 'tours.adminWorkers.step1Body',
      },
      {
        anchor: 'workers-metrics',
        titleKey: 'tours.adminWorkers.step2Title',
        bodyKey: 'tours.adminWorkers.step2Body',
      },
      {
        anchor: 'workers-heartbeat-col',
        titleKey: 'tours.adminWorkers.step3Title',
        bodyKey: 'tours.adminWorkers.step3Body',
      },
      {
        anchor: 'workers-table',
        titleKey: 'tours.adminWorkers.step4Title',
        bodyKey: 'tours.adminWorkers.step4Body',
      },
    ],
  },
  'admin-audit-log': {
    id: 'admin-audit-log',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/shifts/audit',
    steps: [
      {
        anchor: 'nav-shift-audit',
        route: '/shifts/audit',
        titleKey: 'tours.adminAudit.step1Title',
        bodyKey: 'tours.adminAudit.step1Body',
      },
      {
        anchor: 'audit-filters',
        titleKey: 'tours.adminAudit.step2Title',
        bodyKey: 'tours.adminAudit.step2Body',
      },
      {
        anchor: 'audit-table',
        titleKey: 'tours.adminAudit.step3Title',
        bodyKey: 'tours.adminAudit.step3Body',
      },
    ],
  },
  'admin-tenant-flags-read': {
    id: 'admin-tenant-flags-read',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/dashboard',
    steps: [
      {
        anchor: 'nav-shift-reviews',
        route: '/dashboard',
        titleKey: 'tours.adminFlagsRead.step1Title',
        bodyKey: 'tours.adminFlagsRead.step1Body',
      },
      {
        anchor: 'nav-shift-schedule',
        titleKey: 'tours.adminFlagsRead.step2Title',
        bodyKey: 'tours.adminFlagsRead.step2Body',
      },
      {
        anchor: 'nav-help',
        titleKey: 'tours.adminFlagsRead.step3Title',
        bodyKey: 'tours.adminFlagsRead.step3Body',
      },
    ],
  },
  'admin-reports-aging': {
    id: 'admin-reports-aging',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/reports',
    steps: [
      {
        anchor: 'reports-hub',
        route: '/reports',
        titleKey: 'tours.adminAging.step1Title',
        bodyKey: 'tours.adminAging.step1Body',
      },
      {
        anchor: 'reports-aging-card',
        titleKey: 'tours.adminAging.step2Title',
        bodyKey: 'tours.adminAging.step2Body',
      },
      {
        anchor: 'report-aging-kpis',
        route: '/reports/review-queue-aging',
        titleKey: 'tours.adminAging.step3Title',
        bodyKey: 'tours.adminAging.step3Body',
      },
      {
        anchor: 'report-results',
        titleKey: 'tours.adminAging.step4Title',
        bodyKey: 'tours.adminAging.step4Body',
      },
    ],
  },

  // —— Platform P2 ——
  'platform-feedback-inbox': {
    id: 'platform-feedback-inbox',
    roles: ['SuperAdmin'],
    entryRoute: '/platform/feedback',
    steps: [
      {
        anchor: 'nav-platform-feedback',
        route: '/platform/feedback',
        titleKey: 'tours.platformFeedback.step1Title',
        bodyKey: 'tours.platformFeedback.step1Body',
      },
      {
        anchor: 'platform-feedback-filters',
        titleKey: 'tours.platformFeedback.step2Title',
        bodyKey: 'tours.platformFeedback.step2Body',
      },
      {
        anchor: 'platform-feedback-table',
        titleKey: 'tours.platformFeedback.step3Title',
        bodyKey: 'tours.platformFeedback.step3Body',
      },
    ],
  },

  'user-my-leave': {
    id: 'user-my-leave',
    roles: ['User'],
    entryRoute: '/my-leave',
    steps: [
      {
        anchor: 'my-leave-header',
        route: '/my-leave',
        titleKey: 'tours.userMyLeave.step1Title',
        bodyKey: 'tours.userMyLeave.step1Body',
      },
      {
        anchor: 'my-leave-list',
        titleKey: 'tours.userMyLeave.step2Title',
        bodyKey: 'tours.userMyLeave.step2Body',
      },
      {
        anchor: 'my-leave-balances',
        titleKey: 'tours.userMyLeave.step3Title',
        bodyKey: 'tours.userMyLeave.step3Body',
      },
    ],
  },
  'user-request-leave': {
    id: 'user-request-leave',
    roles: ['User'],
    entryRoute: '/my-leave/new',
    steps: [
      {
        anchor: 'request-leave-form',
        route: '/my-leave/new',
        titleKey: 'tours.userRequestLeave.step1Title',
        bodyKey: 'tours.userRequestLeave.step1Body',
      },
      {
        anchor: 'request-leave-submit',
        titleKey: 'tours.userRequestLeave.step2Title',
        bodyKey: 'tours.userRequestLeave.step2Body',
      },
    ],
  },
  'admin-leave-queue': {
    id: 'admin-leave-queue',
    roles: ['TenantAdmin', 'SuperAdmin', 'Supervisor'],
    entryRoute: '/shifts/leave',
    steps: [
      {
        anchor: 'leave-queue-header',
        route: '/shifts/leave',
        titleKey: 'tours.adminLeaveQueue.step1Title',
        bodyKey: 'tours.adminLeaveQueue.step1Body',
      },
      {
        anchor: 'leave-queue-table',
        titleKey: 'tours.adminLeaveQueue.step2Title',
        bodyKey: 'tours.adminLeaveQueue.step2Body',
      },
    ],
  },
  'admin-leave-balances': {
    id: 'admin-leave-balances',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/shifts/time-off/balances',
    steps: [
      {
        anchor: 'leave-balances-header',
        route: '/shifts/time-off/balances',
        titleKey: 'tours.adminLeaveBalances.step1Title',
        bodyKey: 'tours.adminLeaveBalances.step1Body',
      },
      {
        anchor: 'leave-balances-table',
        titleKey: 'tours.adminLeaveBalances.step2Title',
        bodyKey: 'tours.adminLeaveBalances.step2Body',
      },
    ],
  },
  'admin-work-rules-time-off': {
    id: 'admin-work-rules-time-off',
    roles: ['TenantAdmin', 'SuperAdmin'],
    entryRoute: '/shifts/work-rules',
    steps: [
      {
        anchor: 'work-rules-header',
        route: '/shifts/work-rules',
        titleKey: 'tours.adminWorkRulesTimeOff.step1Title',
        bodyKey: 'tours.adminWorkRulesTimeOff.step1Body',
      },
      {
        anchor: 'work-rules-time-off-tab',
        titleKey: 'tours.adminWorkRulesTimeOff.step2Title',
        bodyKey: 'tours.adminWorkRulesTimeOff.step2Body',
      },
    ],
  },
  'admin-open-shift-assign': {
    id: 'admin-open-shift-assign',
    roles: ['TenantAdmin', 'SuperAdmin', 'Supervisor'],
    entryRoute: '/shifts/schedule',
    steps: [
      {
        anchor: 'coverage-strip',
        route: '/shifts/schedule',
        titleKey: 'tours.adminOpenShiftAssign.step1Title',
        bodyKey: 'tours.adminOpenShiftAssign.step1Body',
      },
      {
        anchor: 'coverage-add',
        titleKey: 'tours.adminOpenShiftAssign.step2Title',
        bodyKey: 'tours.adminOpenShiftAssign.step2Body',
      },
      {
        anchor: 'schedule-week-grid',
        titleKey: 'tours.adminOpenShiftAssign.step3Title',
        bodyKey: 'tours.adminOpenShiftAssign.step3Body',
      },
    ],
  },
  'admin-shift-swap': {
    id: 'admin-shift-swap',
    roles: ['TenantAdmin', 'SuperAdmin', 'Supervisor'],
    entryRoute: '/shifts/swaps',
    steps: [
      {
        anchor: 'nav-shift-swaps',
        route: '/shifts/swaps',
        titleKey: 'tours.adminShiftSwap.step1Title',
        bodyKey: 'tours.adminShiftSwap.step1Body',
      },
      {
        anchor: 'swaps-inbox',
        titleKey: 'tours.adminShiftSwap.step2Title',
        bodyKey: 'tours.adminShiftSwap.step2Body',
      },
      {
        anchor: 'coverage-strip',
        route: '/shifts/schedule',
        titleKey: 'tours.adminShiftSwap.step3Title',
        bodyKey: 'tours.adminShiftSwap.step3Body',
      },
    ],
  },
  'platform-enable-time-off': {
    id: 'platform-enable-time-off',
    roles: ['SuperAdmin'],
    entryRoute: '/tenants',
    steps: [
      {
        anchor: 'tenants-manage-features',
        route: '/tenants',
        titleKey: 'tours.platformEnableTimeOff.step1Title',
        bodyKey: 'tours.platformEnableTimeOff.step1Body',
      },
      {
        anchor: 'features-time-off',
        titleKey: 'tours.platformEnableTimeOff.step2Title',
        bodyKey: 'tours.platformEnableTimeOff.step2Body',
      },
    ],
  },
};

export function getTour(tourId: string): TourDef | null {
  return TOURS[tourId] ?? null;
}

/**
 * When the primary spotlight target is absent (e.g. SuperAdmin All-tenants home,
 * Supervisor without infra health), try role-appropriate fallbacks.
 */
const TOUR_ANCHOR_FALLBACKS: Record<string, string[]> = {
  'dashboard-insight': ['platform-insight'],
  'dashboard-attention': ['platform-attention'],
  'dashboard-health': ['dashboard-pulse', 'platform-product', 'platform-pulse'],
};

export function findTourAnchor(anchor: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const candidates = [anchor, ...(TOUR_ANCHOR_FALLBACKS[anchor] ?? [])];
  for (const id of candidates) {
    const el = document.querySelector<HTMLElement>(`[data-tour="${CSS.escape(id)}"]`);
    if (el) return el;
  }
  return null;
}

/**
 * Viewport rect for the tour spotlight. Prefer the union of visible children when
 * the anchor is a grid/flex shell that may still be stretched from a prior layout
 * (e.g. 2×2 metrics → 1×4 after sidebar margin finishes animating).
 */
export function measureTourTargetRect(el: HTMLElement): DOMRect {
  const self = el.getBoundingClientRect();
  const kids = Array.from(el.children).filter(
    (c): c is HTMLElement => c instanceof HTMLElement
  );
  if (kids.length < 2) {
    return DOMRect.fromRect(self);
  }

  let top = Infinity;
  let left = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  let any = false;
  for (const kid of kids) {
    const r = kid.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) continue;
    any = true;
    top = Math.min(top, r.top);
    left = Math.min(left, r.left);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  }
  if (!any) {
    return DOMRect.fromRect(self);
  }

  const union = new DOMRect(left, top, right - left, bottom - top);
  // If the shell is materially taller/wider than its children, trust the children
  // (stale stretch / leftover min-height). Keep self when sizes are within a few px.
  if (self.height > union.height + 12 || self.width > union.width + 24) {
    return union;
  }
  return DOMRect.fromRect(self);
}
